<template>
    <LayoutPage v-if="timer.uuid">
        <template #header>
            <TimerProjectHeader :actions="actions" @publish="handlePublish" />
        </template>

        <template #sidebar>
            <router-view />
        </template>

        <template #preview>
            <TimerProjectPreview />
        </template>

        <template #footer>
            <TimerProjectFooter :actions="actions" @publish="handlePublish" />
        </template>
    </LayoutPage>
    <!-- Timer Creator  -->
    <TimerCreator v-if="isCreatorShown" />
    <!-- First steps Video -->
    <cHelpVideosAfterOnboarding v-else />
    <!-- Modal for renaming -->
    <ModalInput
        v-model:open="showRenameModal"
        v-model:text="projectName"
        :modal-data="modalRenameData"
        @cta="handleRenameProject(projectName)"
        @cancel="showRenameModal = false"
    />
    <!-- Modal for deleting -->
    <ModalConfirm
        v-model="showDeleteModal"
        :modal-data="modalDeleteData"
        @cta="deleteProject"
        @cancel="showDeleteModal = false"
    />
    <!-- MODAL for sharing -->
    <ModalDefault
        v-model="showCodeModal"
        :modal-data="codeModalData"
        @cta="copyCode"
        @cancel="hideCodeModal"
        size="xl"
    >
        <template #icon>
            <CardIcon type="success">
                <CodeIcon />
            </CardIcon>
        </template>

        <div class="mt-5 space-y-5">
            <FormSwitch v-model="codeType" :options="codeTypes" />
            <div class="space-y-3">
                <p class="text-sm font-medium ct-text-2">{{ $t("timer.code") }}</p>
                <CardCode>{{ timerCode }}</CardCode>
            </div>
        </div>
    </ModalDefault>
</template>

<script>
import { markRaw } from "vue";
import { LayoutPage } from "@easy/ui/layouts";
import TimerCreator from "@/apps/timer/project/onboarding/TimerCreator.vue";
import TimerProjectHeader from "@/apps/timer/project/TimerProjectHeader.vue";
import TimerProjectPreview from "@/apps/timer/project/TimerProjectPreview.vue";
import TimerProjectFooter from "@/apps/timer/project/TimerProjectFooter.vue";
import { makeLogger } from "@/shared/consola";
import { mapActions, mapState, mapGetters, mapMutations } from "vuex";
import { PencilLineIcon, TrashBinIcon, InvisibleIcon, CodeIcon } from "@easy/ui/icons";
import { ModalInput, ModalConfirm, ModalDefault } from "@easy/ui/modals";
import { CardIcon, CardCode } from "@easy/ui/cards";
import { FormSwitch } from "@easy/ui/forms";
import { EventBus } from "@/shared/projects/event-bus";
import { copy } from "@/shared/utils/string";
import { useTimerCreatorStore } from "@/apps/timer/project/onboarding/timer-creator.store";
import { storeToRefs } from "pinia";
import cHelpVideosAfterOnboarding from "@/shared/components/help/cHelpVideosAfterOnboarding.vue";

const logger = makeLogger("TimerProjectPage");

export default {
    components: {
        cHelpVideosAfterOnboarding,
        LayoutPage,
        TimerProjectHeader,
        TimerProjectPreview,
        TimerProjectFooter,
        ModalInput,
        ModalConfirm,
        ModalDefault,
        CardIcon,
        CardCode,
        FormSwitch,
        CodeIcon,
        TimerCreator,
    },
    mounted() {
        this.$watch(() => this.$route.params.projectUuid, this.handleProjectChange, {
            immediate: true,
        });
        this.$watch(
            () => this.timer,
            (timer) => {
                this.projectName = timer?.name || "";
                document.title = `Easytimer | ${this.projectName}`;
                if (this.projectName) {
                    this.autoOpenCreator(this.timer);
                }
            },
            { immediate: true }
        );
        // FIXME: walk around for now when (publish)
        EventBus.$on("askForChanges", (resolve) => {
            resolve(false);
        });
    },
    setup() {
        const timerCreatorStore = useTimerCreatorStore();
        const { autoOpenCreator } = timerCreatorStore;
        const { isCreatorShown } = storeToRefs(timerCreatorStore);
        return { isCreatorShown, autoOpenCreator };
    },
    data() {
        return {
            showRenameModal: false,
            showDeleteModal: false,
            projectName: "",
            showCodeModal: false,
            codeType: "html",
            htmlCode: "",
            mailCode: "",
        };
    },
    computed: {
        ...mapState(["globalUrl"]),
        ...mapState("timer", ["timer"]),
        ...mapGetters(["isPublished", "appUrl"]),
        ...mapGetters("timer", ["timerIframe"]),
        actions() {
            return [
                {
                    label: this.$t("shared.copy_embed"),
                    icon: markRaw(CodeIcon),
                    handler: () => {
                        this.handlePublish();
                    },
                    mobileOnly: true,
                },
                {
                    label: this.$t("shared.rename"),
                    icon: markRaw(PencilLineIcon),
                    handler: () => {
                        this.showRenameModal = true;
                    },
                },
                this.isPublished(this.timer)
                    ? {
                          label: this.$t("shared.unpublish"),
                          icon: markRaw(InvisibleIcon),
                          handler: () => {
                              this.unpublishTimer();
                              logger.log("unpublish");
                          },
                      }
                    : {},
                { isDivider: true },
                {
                    label: this.$t("shared.delete"),
                    isDanger: true,
                    icon: markRaw(TrashBinIcon),
                    handler: () => {
                        this.showDeleteModal = true;
                    },
                },
            ];
        },
        modalRenameData() {
            return {
                title: this.$t("shared.rename"),
                inputLabel: this.$t("shared.project_name"),
                ctaLabel: this.$t("shared.save"),
                cancelLabel: this.$t("shared.cancel"),
            };
        },
        modalDeleteData() {
            return {
                title: this.$t("shared.delete_project"),
                description: this.$t("shared.delete_project_info"),
                ctaLabel: this.$t("shared.delete"),
                cancelLabel: this.$t("shared.cancel"),
            };
        },
        codeModalData() {
            return {
                title: this.$t("shared.published"),
                description: this.$t("timer.code_info"),
                ctaLabel: this.$t("shared.copy_code"),
                code: this.timerCode,
            };
        },
        timerCode() {
            return this.codeType === "html" ? this.htmlCode : this.mailCode;
        },
        codeTypes() {
            return [
                {
                    label: this.$t("timer.html_version"),
                    value: "html",
                },
                {
                    label: this.$t("timer.mail_version"),
                    value: "mail",
                },
            ];
        },
    },
    methods: {
        ...mapMutations(["showAlert"]),
        ...mapActions("timer", [
            "publishProject",
            "unpublishTimer",
            "loadProject",
            "loadLabelTranslations",
        ]),
        ...mapActions("project", ["removeProject", "renameProject"]),
        ...mapActions("modals", ["showConfirmModal"]),
        handleProjectChange(uuid, oldUuid) {
            if (uuid && uuid !== oldUuid) {
                this.loadProject(uuid);
                this.loadLabelTranslations();
            }
        },
        async handlePublish() {
            const { timer, htmlTimer } = await this.publishProject();
            this.htmlCode = htmlTimer;
            this.mailCode = timer;
            this.showCodeModal = true;
        },
        async handleRenameProject(newName) {
            // FIXME There is problem with a SNAPSHOT.
            // When you rename project snapshot updates and overrides current data :(
            const error = await this.renameProject({ tool: "timer", project: this.timer, newName });
            if (!error) {
                this.showRenameModal = false;
            }
        },
        async deleteProject() {
            const error = await this.removeProject({ tool: "timer ", uuid: this.timer.uuid });
            if (!error) {
                this.showDeleteModal = false;
            }
        },
        hideCodeModal() {
            this.showCodeModal = false;
        },
        copyCode() {
            copy(this.timerCode);
            this.showAlert({
                type: "success",
                title: this.$t("shared.copied"),
            });
            this.hideCodeModal();
        },
    },
};
</script>
